import React, { useState } from 'react';
import styled from 'styled-components';
import { Mail, Send, CheckCircle2, AlertCircle } from 'lucide-react';
import { contactAPI } from '../services/api';

const ContactContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 120px 20px 40px;
  line-height: 1.6;
  color: #374151;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: #64748b;
  margin-bottom: 32px;
`;

const IntroCard = styled.div`
  background: linear-gradient(135deg, #eff6ff 0%, #f8fafc 100%);
  border: 1px solid #bfdbfe;
  border-radius: 14px;
  padding: 24px;
  margin-bottom: 32px;
`;

const IntroText = styled.p`
  margin: 0 0 12px;
  color: #475569;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Form = styled.form`
  display: grid;
  gap: 20px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 28px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.06);
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
`;

const Field = styled.div`
  display: grid;
  gap: 8px;
`;

const Label = styled.label`
  font-weight: 600;
  color: #1f2937;
`;

const Input = styled.input`
  width: 100%;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 12px 14px;
  font: inherit;
  color: #1f2937;
  background: #fff;

  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 180px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 12px 14px;
  font: inherit;
  color: #1f2937;
  background: #fff;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
  }
`;

const HiddenField = styled.div`
  position: absolute;
  left: -9999px;
  width: 1px;
  height: 1px;
  overflow: hidden;
`;

const HelpText = styled.p`
  margin: -4px 0 0;
  font-size: 0.95rem;
  color: #64748b;
`;

const StatusMessage = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px 16px;
  border-radius: 12px;
  background: ${(props) => (props.$type === 'success' ? '#ecfdf5' : '#fef2f2')};
  border: 1px solid ${(props) => (props.$type === 'success' ? '#86efac' : '#fecaca')};
  color: ${(props) => (props.$type === 'success' ? '#166534' : '#991b1b')};
`;

const SubmitRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const SubmitNote = styled.p`
  margin: 0;
  color: #64748b;
  font-size: 0.95rem;
`;

const SubmitButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: none;
  border-radius: 10px;
  padding: 12px 18px;
  font: inherit;
  font-weight: 600;
  color: white;
  background: ${(props) => (props.disabled ? '#94a3b8' : '#2563eb')};
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: background 0.2s ease, transform 0.2s ease;

  &:hover {
    background: ${(props) => (props.disabled ? '#94a3b8' : '#1d4ed8')};
    transform: ${(props) => (props.disabled ? 'none' : 'translateY(-1px)')};
  }
`;

const initialFormState = {
  name: '',
  email: '',
  subject: '',
  message: '',
  website: '',
};

const Contact = () => {
  const [formData, setFormData] = useState(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState(null);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus(null);
    setIsSubmitting(true);

    try {
      const response = await contactAPI.submit(formData);
      setStatus({
        type: 'success',
        message: response?.data?.message || 'Your message has been sent successfully.',
      });
      setFormData(initialFormState);
    } catch (error) {
      setStatus({
        type: 'error',
        message:
          error?.response?.data?.error ||
          'We could not send your message right now. Please try again in a moment.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ContactContainer>
      <Title>
        <Mail size={32} />
        Contact MicroPinouts
      </Title>

      <Subtitle>
        Send feedback, report an issue, ask a question, or get in touch about collaboration.
      </Subtitle>

      <IntroCard>
        <IntroText>
          Messages from this form are delivered to <strong>admin@micropinouts.com</strong>.
        </IntroText>
        <IntroText>
          If you are reporting a board, wiring page, or pinout issue, include the page URL and as much detail as you can.
        </IntroText>
      </IntroCard>

      <Form onSubmit={handleSubmit}>
        <FieldGrid>
          <Field>
            <Label htmlFor="contact-name">Name</Label>
            <Input
              id="contact-name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              maxLength={120}
              required
            />
          </Field>

          <Field>
            <Label htmlFor="contact-email">Email</Label>
            <Input
              id="contact-email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              maxLength={190}
              required
            />
          </Field>
        </FieldGrid>

        <Field>
          <Label htmlFor="contact-subject">Subject</Label>
          <Input
            id="contact-subject"
            name="subject"
            type="text"
            value={formData.subject}
            onChange={handleChange}
            maxLength={160}
            required
          />
        </Field>

        <Field>
          <Label htmlFor="contact-message">Message</Label>
          <TextArea
            id="contact-message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            minLength={10}
            maxLength={5000}
            required
          />
          <HelpText>
            Include enough context for us to reproduce the issue or answer your question.
          </HelpText>
        </Field>

        <HiddenField aria-hidden="true">
          <Label htmlFor="contact-website">Website</Label>
          <Input
            id="contact-website"
            name="website"
            type="text"
            value={formData.website}
            onChange={handleChange}
            autoComplete="off"
            tabIndex={-1}
          />
        </HiddenField>

        {status && (
          <StatusMessage $type={status.type}>
            {status.type === 'success' ? <CheckCircle2 size={20} /> : <AlertCircle size={20} />}
            <span>{status.message}</span>
          </StatusMessage>
        )}

        <SubmitRow>
          <SubmitNote>
            We use your email only to reply to your message if needed.
          </SubmitNote>
          <SubmitButton type="submit" disabled={isSubmitting}>
            <Send size={18} />
            {isSubmitting ? 'Sending...' : 'Send Message'}
          </SubmitButton>
        </SubmitRow>
      </Form>
    </ContactContainer>
  );
};

export default Contact;
